<template>
    <div class="volunteers-filter space-y-3">
        <!-- Search Input (rounded-full style matching blog/mentori) -->
        <div class="relative flex items-center bg-white dark:bg-surface-card rounded-full border border-gray-300 dark:border-default">
            <input
                :value="modelValue.q"
                type="text"
                class="block w-full h-[52px] rounded-full bg-transparent py-4 pl-5 pr-24 text-lg placeholder-gray-400 dark:placeholder-txt-body text-gray-900 dark:text-white border-transparent focus:outline-none focus:border-transparent focus:ring-0"
                placeholder="Hľadať dobrovoľníkov..."
                @input="update({ q: ($event.target as HTMLInputElement).value })"
                @keydown.enter="update({ q: ($event.target as HTMLInputElement).value })"
            >
            <div class="absolute inset-y-0 right-0 flex items-center gap-2 pr-1.5">
                <!-- Clear button -->
                <button
                    v-if="modelValue.q"
                    type="button"
                    class="text-gray-400 dark:text-txt-body hover:text-gray-600 dark:hover:text-txt-body"
                    @click="update({ q: '' })"
                >
                    <Icon name="heroicons:x-mark" class="size-5" />
                </button>
                <!-- Search button -->
                <button
                    type="button"
                    class="flex items-center justify-center size-10 rounded-full bg-gray-200 dark:bg-subtle hover:bg-gray-300 dark:hover:bg-subtle transition-colors shrink-0"
                >
                    <Icon name="heroicons:magnifying-glass" class="size-5 text-gray-500 dark:text-txt-body" />
                </button>
            </div>
        </div>

        <!-- Results count + Filter toggle row -->
        <FilterToggleRow
            v-model:show-filters="isExpanded"
            :has-active-filters="hasActiveFilters"
            :filter-count="countFilterTypes"
            @reset="resetFilters"
        >
            <slot />
        </FilterToggleRow>

        <!-- Collapsible Filter Panel -->
        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div v-if="isExpanded" class="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-default dark:bg-surface-card/50">
                <!-- Keywords autocomplete -->
                <div>
                    <CFormAutocomplete
                        autoexpand
                        :disabled="isLoadingOptions"
                        no-results-message="Žiadne výsledky pre zadané kľúčové slová"
                        :options="options.interestOptions"
                        placeholder="Kľúčové slová"
                        prefix-icon="hashtag"
                        :model-value="modelValue.interests"
                        @click="trackInputInteraction('interests')"
                        @update:model-value="update({ interests: $event })"
                    />
                </div>

                <div class="grid gap-3 md:grid-cols-3">
                    <FilterDropdownNext
                        :disabled="isLoadingOptions"
                        :options="options.whoOptions"
                        placeholder="Pre koho"
                        :value="modelValue.who"
                        @click="trackInputInteraction('who')"
                        @input="update({ who: $event })"
                    />

                    <FilterDropdownNext
                        :disabled="isLoadingOptions"
                        :options="options.capacityOptions"
                        placeholder="Dĺžka dobrovoľníctva"
                        :value="modelValue.capacity"
                        @click="trackInputInteraction('capacity')"
                        @input="update({ capacity: $event })"
                    />

                    <FilterDropdownNext
                        :disabled="isLoadingOptions"
                        :options="options.regionOptions"
                        placeholder="Lokalita"
                        :value="modelValue.region"
                        @click="trackInputInteraction('region')"
                        @input="update({ region: $event })"
                    />
                </div>

                <div class="grid gap-3 md:grid-cols-2">
                    <FilterDropdownNext
                        :disabled="isLoadingOptions"
                        :options="options.cooperationOptions"
                        placeholder="Forma spolupráce"
                        :value="modelValue.cooperation"
                        @click="trackInputInteraction('cooperation')"
                        @input="update({ cooperation: $event })"
                    />

                    <FilterDropdownNext
                        :disabled="isLoadingOptions"
                        :options="options.prefersFieldOfExperienceOptions"
                        placeholder="Typ dobrovoľníctva"
                        :value="experienceValue"
                        @click="trackInputInteraction('prefersFieldOfExperience')"
                        @input="update({ experience: $event })"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import debounce from 'lodash-es/debounce'

import { VolunteerPrefersFieldOfExperience } from '#shared/types/User'
import { capacity, cooperationType, helpWho, slovakRegionOptions } from '#shared/types/VolunteerOptions'
import FilterDropdownNext from '@/components/filters/components/FilterDropdownNext.vue'
import { useVolunteers } from '@/composables/volunteers/useVolunteers'

const props = defineProps({
    modelValue: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['update:modelValue'])
const { loadVolunteerFacets } = useVolunteers()

const isExpanded = ref(false)
const isLoadingOptions = ref(true)
const options = ref<Record<string, any>>({})

const countFilterTypes = computed((): number => {
    // Count filters excluding 'q' (search query) and empty arrays
    return Object.entries(props.modelValue).filter(([key, value]) => {
        if (key === 'q') return false
        if (Array.isArray(value)) return value.length > 0
        return !!value
    }).length
})

const hasActiveFilters = computed(() => {
    return Object.entries(props.modelValue).some(([key, value]) => {
        if (key === 'q') return false
        if (Array.isArray(value)) return value.length > 0
        return !!value
    })
})

const updateDebounced = computed(() => {
    return debounce((change: any) => update(change), 300)
})

// Remove EITHER if there isn't "yes" or "no" values selected
const experienceValue = computed((): VolunteerPrefersFieldOfExperience[] => {
    if (!props.modelValue.experience) {
        return []
    }

    const value = props.modelValue.experience.filter(
        (experience: VolunteerPrefersFieldOfExperience) => experience !== VolunteerPrefersFieldOfExperience.EITHER
    )

    return value
})

// Define prefersFieldOfExperience options locally since it's not exported from VolunteerOptions
const prefersFieldOfExperience = [
    {
        id: VolunteerPrefersFieldOfExperience.YES,
        value: VolunteerPrefersFieldOfExperience.YES,
        label: 'hlavou'
    },
    {
        id: VolunteerPrefersFieldOfExperience.NO,
        value: VolunteerPrefersFieldOfExperience.NO,
        label: 'rukami'
    }
]

onMounted(() => {
    loadOptions()
})

async function loadOptions() {
    isLoadingOptions.value = true

    try {
        // Retrieve filtering data - using flat field names (not nested)
        const facets = await loadVolunteerFacets({
            facets: [
                'volunteerInterests',
                'volunteerHelpWho',
                'volunteerTimeCapacity',
                'volunteerCooperation',
                'volunteerGeoRegion',
                'volunteerPrefersFieldOfExperience'
            ]
        })

        const interestOptions = Object.keys(facets['volunteerInterests'] || {})

        const whoOptions = Object.values(helpWho).map((who) => ({
            id: who.id,
            label: who.label,
            disabled: !Object.keys(facets['volunteerHelpWho'] || {}).includes(who.id)
        }))

        const capacityOptions = Object.values(capacity).map((capacity) => ({
            id: capacity.id,
            label: capacity.label,
            disabled: !Object.keys(facets['volunteerTimeCapacity'] || {}).includes(capacity.id)
        }))

        const cooperationOptions = Object.values(cooperationType).map((cooperation) => ({
            id: cooperation.id,
            label: cooperation.label,
            disabled: !Object.keys(facets['volunteerCooperation'] || {}).includes(cooperation.id)
        }))

        const regionOptions = Object.values(slovakRegionOptions).map((region) => ({
            id: region.id,
            label: region.label,
            disabled: !Object.keys(facets['volunteerGeoRegion'] || {}).includes(region.id)
        }))

        const prefersFieldOfExperienceOptions = Object.values(prefersFieldOfExperience).map((prefersFieldOfExperience) => ({
            id: prefersFieldOfExperience.id,
            label: prefersFieldOfExperience.label,
            disabled: !Object.keys(facets['volunteerPrefersFieldOfExperience'] || {}).includes(
                prefersFieldOfExperience.id
            )
        }))

        options.value = {
            interestOptions,
            whoOptions,
            capacityOptions,
            cooperationOptions,
            regionOptions,
            prefersFieldOfExperienceOptions
        }
    } catch (error) {
        console.error('Error loading volunteer filter options:', error)
        // Set default options so filters are still usable
        options.value = {
            interestOptions: [],
            whoOptions: Object.values(helpWho).map((who) => ({ id: who.id, label: who.label, disabled: false })),
            capacityOptions: Object.values(capacity).map((c) => ({ id: c.id, label: c.label, disabled: false })),
            cooperationOptions: Object.values(cooperationType).map((c) => ({ id: c.id, label: c.label, disabled: false })),
            regionOptions: Object.values(slovakRegionOptions).map((r) => ({ id: r.id, label: r.label, disabled: false })),
            prefersFieldOfExperienceOptions: Object.values(prefersFieldOfExperience).map((p) => ({ id: p.id, label: p.label, disabled: false }))
        }
    } finally {
        isLoadingOptions.value = false
    }
}

function update(change: any) {
    if (change.experience) {
        // Only add EITHER if there are "yes" or "no" values selected
        if (!Array.isArray(change.experience)) {
            change.experience = [change.experience]
        } else if (
            !change.experience.includes(VolunteerPrefersFieldOfExperience.EITHER) &&
            (change.experience.includes(VolunteerPrefersFieldOfExperience.YES) ||
                change.experience.includes(VolunteerPrefersFieldOfExperience.NO))
        ) {
            change.experience.push(VolunteerPrefersFieldOfExperience.EITHER)
        }
    }

    emit('update:modelValue', { ...props.modelValue, ...change })

    // const [field, value] = Object.entries(change)[0]
    // this.$exponea.track('volunteer_search', {
    //     action: 'select',
    //     field,
    //     value
    // })
}

function resetFilters() {
    emit('update:modelValue', {})

    // this.$exponea.track('volunteer_search', { action: 'reset' })
}

function toggle() {
    isExpanded.value = !isExpanded.value
}

function trackInputInteraction(_event: string) {
    // this.$exponea.track('volunteer_search', { action: 'browse', field })
}
</script>
